import type { GridColDef } from '@mui/x-data-grid-pro';
import {
  FORMULA_BUILT_IN_FUNCTIONS,
  createFormulaFunctionRegistry,
  createFormulaParser,
} from './engine';
import type {
  GridFormulaFunctionDefinition,
  GridFormulaInternalCache,
} from './gridFormulaInterfaces';
import type { DataGridPremiumProcessedProps } from '../../../models/dataGridPremiumProps';

/**
 * The built-in formula functions.
 * The `formulaFunctions` prop has replacement semantics: spread this object to extend it.
 */
export const GRID_FORMULA_FUNCTIONS: Record<string, GridFormulaFunctionDefinition> =
  Object.fromEntries(FORMULA_BUILT_IN_FUNCTIONS.map((definition) => [definition.name, definition]));

const EMPTY_FORMULA_FUNCTIONS: Record<string, GridFormulaFunctionDefinition> = {};

/**
 * Resolves the `formulaFunctions` prop default. The default lives here — not in
 * `useDataGridPremiumProps` — so the grid does not bundle the built-in function
 * set (and, through it, the engine) when the formula feature is not injected.
 * Stable identities: the return value is compared by identity against the
 * registry source cache.
 */
export function getEffectiveFormulaFunctions(
  props: Pick<DataGridPremiumProcessedProps, 'formulaFunctions' | 'dataSource'>,
): Record<string, GridFormulaFunctionDefinition> {
  if (props.formulaFunctions !== undefined) {
    return props.formulaFunctions;
  }
  return props.dataSource ? EMPTY_FORMULA_FUNCTIONS : GRID_FORMULA_FUNCTIONS;
}

export function createFormulaInternalCache(
  formulaFunctions: Record<string, GridFormulaFunctionDefinition>,
): GridFormulaInternalCache {
  return {
    parser: createFormulaParser(),
    registry: createFormulaFunctionRegistry(Object.values(formulaFunctions)),
    registrySource: formulaFunctions,
    records: new Map(),
    dependents: new Map(),
    dependentsByRowId: new Map(),
    recordsByField: new Map(),
    positionDependentKeys: new Set(),
    rangeDependentsByField: new Map(),
    positionContext: null,
    positionContextRowIds: null,
    positionContextFields: null,
    positionContextVersion: 0,
    suppressRegroupTrigger: false,
    trackedValues: new Map(),
    lastRowIdToModelLookup: null,
    formulaFields: [],
    lastColumnsSignature: new Map(),
    lastCellEditStart: null,
    lastA1Seed: null,
    pasteOrigin: null,
    plainEditDraft: null,
    editorSession: null,
    focusSafeElements: new Set(),
  };
}

export function resetFormulaEvaluationCache(cache: GridFormulaInternalCache) {
  cache.records = new Map();
  cache.dependents = new Map();
  cache.dependentsByRowId = new Map();
  cache.recordsByField = new Map();
  cache.positionDependentKeys = new Set();
  cache.rangeDependentsByField = new Map();
  // The version counter survives: it must keep increasing across resets.
  cache.positionContext = null;
  cache.positionContextRowIds = null;
  cache.positionContextFields = null;
  cache.trackedValues = new Map();
  cache.lastRowIdToModelLookup = null;
}

export function getFormulaFields(columnsLookup: Record<string, GridColDef>): string[] {
  const fields: string[] = [];
  for (const field of Object.keys(columnsLookup)) {
    if (columnsLookup[field].allowFormulas) {
      fields.push(field);
    }
  }
  return fields;
}

export function areFormulaFieldsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * The column inputs evaluation depends on: field existence and `valueGetter`
 * identity (raw dependency reads go through it).
 */
export function computeColumnsSignature(
  columnsLookup: Record<string, GridColDef>,
): Map<string, unknown> {
  const signature = new Map<string, unknown>();
  for (const field of Object.keys(columnsLookup)) {
    signature.set(field, columnsLookup[field].valueGetter);
  }
  return signature;
}

export function areColumnsSignaturesEqual(
  a: Map<string, unknown>,
  b: Map<string, unknown>,
): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const [field, valueGetter] of a) {
    if (!b.has(field) || b.get(field) !== valueGetter) {
      return false;
    }
  }
  return true;
}

export function areFormulaFunctionRecordsEqual(
  a: Record<string, GridFormulaFunctionDefinition>,
  b: Record<string, GridFormulaFunctionDefinition>,
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const key of aKeys) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}
